import { FC, useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import { selectDailyNavApplyDates, selectDailyNavDayConfig } from 'common/constants/dailyNav';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomScalarSpreadsheet, MessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { TertiaryNavigationActionControlConfig } from 'context/FirmBaseLayoutContext';
import { useGetDailyNavConfigApplyPreview } from 'services/hooks/dailyNav';
import {
  useGenerateDailyNavRecords,
  useGetCompanyListByFirm,
  useGetDailyNavPreviousValuations,
  useGetDailyNavRecords,
} from 'services/hooks/firms';
import { getArrayValue, getBooleanValue, getObjectValue } from 'utilities';
import DayConfigBanner from './components/DayConfigBanner';
import DeltaBreakdownDialog from './components/DeltaBreakdownDialog';
import FirmRulesIcon from './components/FirmRulesIcon';
import ReportDateSelector from './components/ReportDateSelector';
import ReviewPriceDialog from './components/ReviewPriceDialog';
import SecondaryBreakdownDialog from './components/SecondaryBreakdownDialog';
import { DAILY_NAV_REPORT_DATE_PARAM } from './dailyNav.constants';
import { DailyNavTableRow, useDailyNavSheet } from './hooks/useDailyNavSheet';

const DailyNav: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo, user } = getObjectValue(storeValue);
  const { id: firmId, slug: firmSlug } = getObjectValue(firmInfo);
  // Generating records is a staff-only tool. Reviewing a price mirrors the backend
  // DailyNavRecord update endpoint, which allows firm editors plus Scalar staff.
  const isStaffOrSuperuser = getBooleanValue(user?.is_staff) || getBooleanValue(user?.is_superuser);
  const { canMutateFirmScopedEntities } = useFirmScopedPermissions({ firmId });
  const canReviewPrice = canMutateFirmScopedEntities || isStaffOrSuperuser;

  const [reviewRow, setReviewRow] = useState<DailyNavTableRow | null>(null);
  const [breakdownRow, setBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [secondaryBreakdownRow, setSecondaryBreakdownRow] = useState<DailyNavTableRow | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get(DAILY_NAV_REPORT_DATE_PARAM) ?? undefined;

  const setSelectedDate = useCallback(
    (date: string) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set(DAILY_NAV_REPORT_DATE_PARAM, date);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { mutate: generateDailyNavRecords, isPending: isGenerating } = useGenerateDailyNavRecords(firmId);
  const { data: companies } = useGetCompanyListByFirm({ firmId });
  // With no report date selected, the backend resolves the current Eastern-time calendar day.
  const { data: dailyNavRecords, isLoading: isDailyNavRecordsLoading } = useGetDailyNavRecords({
    firmId,
    date: selectedDate,
  });

  // The records carry whichever date the backend settled on; it also scopes the Looking Glass
  // lookup and fills the Report date control before the user has picked anything.
  const resolvedDate = dailyNavRecords?.[0]?.date;
  const reportDate = selectedDate ?? resolvedDate;

  // The previous valuation per company: the backend returns the latest FINAL company
  // rollup strictly before the report date — one row per company — so the client no
  // longer loads the whole snapshot history to dedupe it. Fields must be explicit;
  // the endpoint's default set omits equity_value and percent change.
  const { data: previousValuations } = useGetDailyNavPreviousValuations({
    firmId,
    date: reportDate,
    fields: ['equity_value', 'percent_change_from_previous', 'common_share_value', 'cap_table_currency'],
    shouldQueryAutomatically: Boolean(firmId) && Boolean(reportDate),
  });

  // Which rules produced the rows on screen -- rides on the records, so no extra
  // request and it still answers when a breakdown came back null.
  //
  // "Is this the day in progress" comes from the preview, never from comparing the
  // report date to today: a date can BE today and already past its market's close.
  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const isOpenNavDay = useMemo(
    () =>
      Boolean(
        reportDate &&
          selectDailyNavApplyDates(applyPreview?.dates).applicableDates.some(date => date.nav_date === reportDate)
      ),
    [applyPreview?.dates, reportDate]
  );
  const { config: dayConfig, isStale, unanchoredCount } = useMemo(
    () => selectDailyNavDayConfig(dailyNavRecords, isOpenNavDay),
    [dailyNavRecords, isOpenNavDay]
  );

  const leftActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ReportDateSelector value={reportDate} onChange={setSelectedDate} />
        {/* Describes THAT day's rules: hover reads them, click opens the settings. */}
        <FirmRulesIcon config={dayConfig} settingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined} />
      </Box>
    ),
    [reportDate, setSelectedDate, dayConfig, firmSlug]
  );

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(
    () =>
      isStaffOrSuperuser
        ? [
          {
            type: 'button',
            label: 'Generate Daily NAVs',
            variant: 'primary',
            disabled: isGenerating || !firmId,
            testId: 'generate-daily-nav-records-button',
            onClick: () => generateDailyNavRecords(selectedDate),
          },
        ]
        : [],
    [firmId, generateDailyNavRecords, isGenerating, isStaffOrSuperuser, selectedDate]
  );

  const tertiaryNavigation = useMemo(
    () => ({
      ariaLabel: 'Daily NAV controls',
      actions: tertiaryNavActions,
      leftActionsNode,
    }),
    [leftActionsNode, tertiaryNavActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  const { spreadsheet } = useDailyNavSheet({
    companies,
    firmSlug,
    records: dailyNavRecords,
    previousValuations,
    onReviewClick: setReviewRow,
    canReview: canReviewPrice,
    onBreakdownClick: setBreakdownRow,
    onSecondaryBreakdownClick: setSecondaryBreakdownRow,
  });

  const spreadsheets = useMemo(() => (spreadsheet?.rowConfig?.length ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const sheetProps = useMemo(() => {
    if (!spreadsheet) {
      return null;
    }
    return {
      ...spreadsheet,
      data: getArrayValue(spreadsheet.data),
      onChange,
      sheet: spreadsheet,
      workbook,
    } as unknown as typeof ScalarSpreadsheet;
  }, [spreadsheet, onChange, workbook]);

  return (
    <Box>
      {!isDailyNavRecordsLoading && (
        <DayConfigBanner
          config={dayConfig}
          isStale={isStale}
          unanchoredCount={unanchoredCount}
          firmSettingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined}
        />
      )}

      {isDailyNavRecordsLoading && <GridSkeleton maxColumns={10} maxRows={4} />}

      {!isDailyNavRecordsLoading && isEmpty(dailyNavRecords) && (
        <MessageBox
          fullWidth={false}
          title="Daily NAV"
          // Only staff see the Generate action, so don't point other users at a button they don't have.
          tagline={
            isStaffOrSuperuser
              ? 'No Daily NAV records yet for this date. Use Generate Daily NAVs to create them.'
              : 'No Daily NAV records yet for this date.'
          }
        />
      )}

      {sheetProps && (
        <WorkbookContext.Provider value={workbookContextValue}>
          <CustomScalarSpreadsheet {...sheetProps} isLoading={isDailyNavRecordsLoading} />
        </WorkbookContext.Provider>
      )}

      <ReviewPriceDialog row={reviewRow} open={Boolean(reviewRow)} onClose={() => setReviewRow(null)} />
      <DeltaBreakdownDialog row={breakdownRow} open={Boolean(breakdownRow)} onClose={() => setBreakdownRow(null)} />
      <SecondaryBreakdownDialog
        row={secondaryBreakdownRow}
        open={Boolean(secondaryBreakdownRow)}
        onClose={() => setSecondaryBreakdownRow(null)}
      />
    </Box>
  );
};

export default DailyNav;
